import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Spin } from 'antd';
import styled from '@emotion/styled';
import OptimizePlanForm, { StyledSelectWithInfiniteScroller } from './OptimizePlanForm';
import { OptimizeOption, useOptimizeOptions } from 'hooks/tradePromoOptimization/useOptimizeOptions';
import OptimizePlanTable from './OptimizePlanTable/OptimizePlanTable';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import _, { set } from 'lodash';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { sendNotification } from 'utils/helperMethods';
import { TableHeader } from './OptimizePlanTable/OptimizePlanTableHeader';
import { InputTypes } from './OptimizePlanTable/OptimizeTableContent';
import dayjs from 'dayjs';
import { antIcon } from 'components/pages/Login';
import { useWindowSize } from 'usehooks-ts';
import KPISelector from '../AddEventModal/PromoSections/KPISelector';
import { queryClient } from 'services/reactQueryService';
import ScopeOfOptimization from './ScopeOfOptimization';
import { TPODataContext } from 'contexts/TPOContext';

type Props = {
    open: boolean;
    onCancel: () => void;
    onSubmitFinish: (data: any) => void;
    planId: number;
    planYear: number;
    isTpmPlan: boolean;
};

export type Level = 'category' | 'brand' | 'sub_brand';
export interface FormData {
    objective: string | null;
    start_date: number | null;
    max_promo_weeks?: number | null;
    keep_top: number | null;
    no_of_concurrent_tprs: number | null;
    no_of_concurrent_fds: number | null;
    no_of_concurrent_mdfs: number | null;
}

export interface OptimisationPlanData {
    objectiveConstraint?: boolean;
    name: string;
    label: string;
    default_value?: number | null;
    min_value: number | null;
    max_value: number | null;
    single_value?: number | null;
    type: InputTypes;
    optimized?: number;
    variance?: number;
    originalValue: number;
    levelDetails?: OptimisationPlanData[];
}

interface IOptimizeApiData {
    level: Level;
    constraint_details: IApiConstraintDetails[];
}

export interface IApiConstraintDetails {
    name: string;
    label: string;
    level_details: ILevelDetails[];
    objective_constraint?: boolean;
    constraint_name?: string;
}

interface ILevelDetails {
    attribute_name: string;
    details: {
        original_value: number;
        min?: number;
        max?: number;
        default_value?: number | null;
        objective_constraint?: boolean;
        optimized_value?: number;
    };
}

interface IOtimizeParsedData {
    label: string;
    name: string;
    type: InputTypes;
    value: number | null;
    levelDetails?: IOtimizeParsedData[];
}

const CustomModal = styled(Modal)`
    .ant-modal-body {
        padding: 0;
    }
    .ant-modal-content {
        padding: 12px 16px;
        border-radius: 8px !important;
    }
    .ant-modal-header {
        padding: 8px 12px;

        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
        padding: 16px 0 8px 0;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
`;

const tableHeaders: TableHeader[] = [
    { key: 'kpi', label: 'KPI', allowSort: false },
    { key: 'orgVal', label: 'Original Value', allowSort: false },
    { key: 'val', label: 'Value', allowSort: false },
];

const OptimizePlanModal: FC<Props> = ({ open, onCancel, onSubmitFinish, planId, planYear, isTpmPlan }) => {
    const DEFAULT_PROMO_WEEKS = 24;
    const [tableData, setTableData] = useState<OptimisationPlanData[]>([]);
    const [isPopoverVisible, setIsPopoverVisible] = useState<boolean>(false);
    const [maxPromoWeeks, setMaxPromoWeeks] = useState(DEFAULT_PROMO_WEEKS);
    const [formData, setFormData] = useState<FormData>({
        start_date: null,
        objective: null,
        keep_top: null,
        no_of_concurrent_tprs: null,
        no_of_concurrent_fds: null,
        no_of_concurrent_mdfs: null,
    });
    const [level, setLevel] = useState<Level>('category');
    const [kpiList, setKpiList] = useState<IOtimizeParsedData[]>([]);
    const { width, height } = useWindowSize();
    const percentageHeight = height * 0.9;
    const percentageWidth = width * 0.8;
    const [errorExists, setErrorExists] = useState(false);
    const [constraintKpi, setConstraintKpi] = useState<IOtimizeParsedData | null>(null);
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
    const [selectedSubBrands, setSelectedSubBrands] = useState<string[]>([]);
    const { selectedPlanInfo } = useContext(TPODataContext);
    const { optimizeOptions } = useOptimizeOptions();

    const { data: attributeFilters, loading: attributeFiltersLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: open && Boolean(planId) },
        queryKey: ['tpo-get-attribute-filters', planId],
        url: `${tradePromoOptimizationUrls.getAttributeFiltersForOptimization}/${planId}`,
        parserFunction: (res: any) => {
            return res?.brands;
        },
    });

    const requiredFormDataPayload = useMemo(() => {
        if (!formData.start_date || !formData.objective) {
            return null;
        }
        if (level === 'category') {
            return {
                objective: formData.objective,
                start_date: formData.start_date,
                plan_id: planId,
                is_tpm_plan: isTpmPlan,
            };
        } else if (level === 'brand') {
            return {
                objective: formData.objective,
                start_date: formData.start_date,
                plan_id: planId,
                is_tpm_plan: isTpmPlan,
                level: level,
                attribute_list: selectedBrands,
            };
        } else if (level === 'sub_brand') {
            return {
                objective: formData.objective,
                start_date: formData.start_date,
                plan_id: planId,
                is_tpm_plan: isTpmPlan,
                level: level,
                attribute_list: selectedSubBrands,
            };
        }
        return null;
    }, [formData.objective, formData.start_date, level, selectedBrands, selectedSubBrands]);

    const levelOptions = useMemo(
        () => [
            {
                label: 'Category',
                value: 'category',
                disabled: selectedBrands.length > 0 || selectedSubBrands.length > 0,
            },
            { label: 'Brand', value: 'brand', disabled: selectedSubBrands.length > 0 },
            { label: 'Sub-Brand', value: 'sub_brand' },
        ],
        [selectedBrands.length, selectedSubBrands.length],
    );

    const { loading: tableContentLoading, isError } = useCommonApiHandler({
        method: 'post',
        queryKey: ['get_optimization_org_values', requiredFormDataPayload],
        queryConfig: { enabled: Boolean(requiredFormDataPayload) },
        url: `${tradePromoOptimizationUrls.getOptimizationOrgValues}`,
        variables: requiredFormDataPayload,
        parserFunction: (res: { data: IOptimizeApiData; error?: any }) => {
            try {
                setErrorExists(false);
                const parsedData = res.data.constraint_details.reduce((acc: IOtimizeParsedData[], item) => {
                    if (
                        item.name !==
                        optimizeOptions.find((obj: OptimizeOption) => obj.value === formData.objective)?.constraintKey
                    ) {
                        const parsedDataItem = getParseDataItem(level, item);
                        acc.push(parsedDataItem);
                    } else {
                        setConstraintKpi(getParseDataItem(level, item));
                    }
                    return acc;
                }, []);
                if (tableData?.length > 0) {
                    const updatedData = tableData.map((dataItem: OptimisationPlanData) => {
                        const updatedDataItem = retainKpiAndUpdateValue(parsedData, dataItem);
                        return updatedDataItem;
                    });
                    setTableData(updatedData);
                }
                setKpiList(parsedData);
                return res;
            } catch (error) {
                setErrorExists(true);
                throw error;
            }
        },
    });

    const retainKpiAndUpdateValue = (parsedData: IOtimizeParsedData[], dataItem: OptimisationPlanData) => {
        const matchingDataItem = parsedData.find((item) => item.name === dataItem.name);
        const originalValue = matchingDataItem?.value ?? 0;
        const delta = Math.abs(originalValue) * 0.2;
        let updatedLevelDetails: OptimisationPlanData[] = [];
        if (level !== 'category' && matchingDataItem?.levelDetails?.length) {
            updatedLevelDetails = matchingDataItem?.levelDetails?.map((detail) => {
                const value = detail.value ?? 0;
                const levelValueDelta = Math.abs(value) * 0.2;
                return {
                    name: detail.name,
                    label: detail.label,
                    type: detail.type,
                    originalValue: value,
                    min_value: _.round(value - levelValueDelta, 2),
                    max_value: _.round(value + levelValueDelta, 2),
                    default_value: null,
                };
            });
        }
        return {
            ...dataItem,
            originalValue,
            min_value: _.round(originalValue - delta, 2),
            max_value: _.round(originalValue + delta, 2),
            levelDetails: updatedLevelDetails,
        };
    };

    const getParseDataItem = (optimizationLevel: Level, dataItem: IApiConstraintDetails): IOtimizeParsedData => {
        if (optimizationLevel === 'category') {
            return {
                label: dataItem.label,
                name: dataItem.name,
                type: InputTypes.Range,
                value: dataItem.level_details[0].details.original_value,
            };
        } else {
            return {
                label: dataItem.label,
                name: dataItem.name,
                type: InputTypes.Range,
                value: null,
                levelDetails: dataItem.level_details.map((levelDetail: ILevelDetails) => ({
                    label: levelDetail.attribute_name,
                    name: levelDetail.attribute_name,
                    type: InputTypes.Range,
                    value: levelDetail.details.original_value,
                })),
            };
        }
    };
    useEffect(() => {
        if (formData.objective && formData.start_date && errorExists) {
            setErrorExists(false);
        }
    }, [formData]);

    useEffect(() => {
        if (formData.objective && tableData.length > 0) {
            const newTableData = tableData.filter(
                (item) =>
                    item.name !==
                    optimizeOptions.find((obj: OptimizeOption) => obj.value === formData.objective)?.constraintKey,
            );
            setTableData(newTableData);
        }
    }, [formData.objective]);

    const { mutate: runOptimization, loading: runOptimizationLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.optimizePlan,
        method: 'post',
    });

    const getLevelDetailsPayload = (attributeName: string, kpiData: OptimisationPlanData) => {
        if (level === 'category') {
            return [
                {
                    attribute_name: attributeName,
                    details: {
                        original_value: kpiData.originalValue,
                        min: kpiData.min_value,
                        max: kpiData.max_value,
                        default_value: null,
                        objective_constraint: false,
                    },
                },
            ];
        } else {
            return kpiData.levelDetails?.map((item) => {
                return {
                    attribute_name: item.name,
                    details: {
                        original_value: item.originalValue,
                        min: item.min_value,
                        max: item.max_value,
                        default_value: null,
                        objective_constraint: false,
                    },
                };
            });
        }
    };

    const getObjectiveConstraintsPayload = () => {
        if (level === 'category') {
            return {
                constraint_name: constraintKpi?.label,
                level_details: [
                    {
                        attribute_name: constraintKpi?.name,
                        details: {
                            original_value: constraintKpi?.value ?? 0,
                            min: constraintKpi?.value,
                            max: constraintKpi?.value,
                            default_value: null,
                            objective_constraint: true,
                        },
                    },
                ],
            };
        } else {
            return {
                constraint_name: constraintKpi?.label,
                level_details: constraintKpi?.levelDetails?.map((item) => {
                    return {
                        attribute_name: item.name,
                        details: {
                            original_value: item.value,
                            min: item.value,
                            max: item.value,
                            default_value: null,
                            objective_constraint: true,
                        },
                    };
                }),
            };
        }
    };

    const onSubmit = () => {
        const maxPromoWeeksValue = Number(maxPromoWeeks || 0);
        if (maxPromoWeeksValue < 0 || maxPromoWeeksValue > 52) {
            sendNotification('error', 'Max Promo Weeks must be between 0 and 52');
            return;
        }
        const objectiveConstraint = getObjectiveConstraintsPayload();
        const apiPayload = {
            plan_id: planId,
            is_tpm_plan: isTpmPlan,
            objective_function: formData.objective,
            plan_start_timestamp: dayjs(`${planYear}-01-01`).startOf('year').unix(),
            plan_end_timestamp: dayjs(`${planYear}-01-01`).endOf('year').unix(),
            optimization_start_timestamp: formData.start_date,
            max_promo_weeks: maxPromoWeeks,
            asper_customer: process.env.REACT_APP_TENANT_ID,
            keep_top: formData.keep_top,
            concurrent_tprs: formData.no_of_concurrent_tprs,
            concurrent_fds: formData.no_of_concurrent_fds,
            concurrent_mdfs: formData.no_of_concurrent_mdfs,
            optimization_attribute_level: level !== 'category' ? level : null,
            constraints: [
                objectiveConstraint,
                ...tableData.map((item: OptimisationPlanData) => ({
                    constraint_name: item.label,
                    level_details: getLevelDetailsPayload(item.name, item),
                })),
            ],
            baseline_volume_version: selectedPlanInfo?.baselineVolumeVersion,
        };
        runOptimization(apiPayload, {
            onSuccess: (data: any) => {
                setFormData({
                    start_date: null,
                    objective: null,
                    keep_top: null,
                    no_of_concurrent_tprs: null,
                    no_of_concurrent_fds: null,
                    no_of_concurrent_mdfs: null,
                });
                setMaxPromoWeeks(DEFAULT_PROMO_WEEKS);
                setTableData([]);
                setSelectedBrands([]);
                setSelectedSubBrands([]);
                setLevel('category');
                onSubmitFinish(data);
            },
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.detail || 'Something Went Wrong';
                //show error message
                sendNotification('error', errorMessage);
            },
        });
    };

    const onKPISelectHandler = (updatedList: string[]) => {
        const selectedKpiData: OptimisationPlanData[] = [];

        setKpiList((prev) =>
            prev.map((kpi) => {
                if (updatedList.includes(kpi.name)) {
                    const value = kpi.value ?? 0;
                    const delta = Math.abs(value * 0.2);
                    const minValue = _.round(value - delta, 2);
                    const maxValue = _.round(value + delta, 2);
                    selectedKpiData.push({
                        name: kpi.name,
                        label: kpi.label,
                        type: kpi.type,
                        originalValue: value,
                        min_value: minValue,
                        max_value: maxValue,
                        default_value: null,
                        single_value: kpi.value,
                        levelDetails: kpi.levelDetails?.map((item) => {
                            const value = item.value ?? 0;
                            const levelValueDelta = Math.abs(value * 0.2);
                            const minValue = _.round(value - levelValueDelta, 2);
                            const maxValue = _.round(value + levelValueDelta, 2);
                            return {
                                name: item.name,
                                label: item.label,
                                type: item.type,
                                originalValue: value,
                                min_value: minValue,
                                max_value: maxValue,
                                default_value: null,
                            };
                        }),
                    });
                }
                return { ...kpi, isSelected: updatedList.includes(kpi.name) };
            }),
        );

        setTableData(selectedKpiData);
    };

    //reset all fields to default when popup closes
    useEffect(() => {
        if (open) {
            //clear cache
            queryClient.removeQueries({
                exact: false,
                queryKey: ['get_optimization_org_values'],
            });
            setErrorExists(false);
        }
    }, [open]);

    return (
        <div>
            <CustomModal
                style={{
                    borderRadius: 8,
                    height: `calc(${percentageHeight}px)`,
                }}
                bodyStyle={{ height: `calc(${percentageHeight}px - 134px)` }}
                width={percentageWidth}
                cancelButtonProps={{ style: { borderRadius: 6 } }}
                okButtonProps={{
                    htmlType: 'submit',
                    disabled:
                        errorExists ||
                        runOptimizationLoading ||
                        tableContentLoading ||
                        !(formData.start_date && formData.objective),
                    style: { borderRadius: 6, minWidth: '140px' },
                    className: 'disabled:bg-red-500 disabled:opacity-50',
                }}
                okText={runOptimizationLoading ? <Spin indicator={antIcon} /> : 'Run Optimization'}
                maskClosable={false}
                destroyOnClose
                centered
                onOk={onSubmit}
                open={open}
                onCancel={() => {
                    setFormData({
                        start_date: null,
                        objective: null,
                        keep_top: null,
                        no_of_concurrent_tprs: null,
                        no_of_concurrent_fds: null,
                        no_of_concurrent_mdfs: null,
                    });
                    setSelectedBrands([]);
                    setSelectedSubBrands([]);
                    setTableData([]);
                    setLevel('category');
                    setMaxPromoWeeks(DEFAULT_PROMO_WEEKS);
                    onCancel();
                }}
                title="Optimize"
            >
                <div className="h-full flex flex-col gap-3 overflow-hidden">
                    <ScopeOfOptimization
                        selectedBrands={selectedBrands}
                        setSelectedBrands={setSelectedBrands}
                        selectedSubBrands={selectedSubBrands}
                        setSelectedSubBrands={setSelectedSubBrands}
                        attributeFilters={attributeFilters}
                        attributeFiltersLoading={attributeFiltersLoading}
                        setLevel={setLevel}
                    />
                    <OptimizePlanForm
                        formData={formData}
                        setFormData={setFormData}
                        setMaxPromoWeeks={setMaxPromoWeeks}
                        maxPromoWeeks={maxPromoWeeks}
                    />

                    <div className="bg-backgroundGreyAlt rounded px-3 py-4 flex-1 flex flex-col overflow-hidden min-h-0">
                        <span className="font-semibold">Constraints</span>
                        <div className="flex justify-between items-center my-3">
                            <div className="text-sm flex gap-3 items-center pl-6">
                                <div className="text-sm flex gap-1 items-center">
                                    <span className="text-redTriangle">*</span>
                                    Level:
                                </div>
                                <StyledSelectWithInfiniteScroller
                                    hasMore={false}
                                    showSearch={false}
                                    filterOption={false}
                                    placeholder={'Select Level'}
                                    maxTagCount="responsive"
                                    className="dropdown-height !h-8 !w-48"
                                    showArrow
                                    onChange={(val) => {
                                        setLevel(val);
                                    }}
                                    value={level}
                                    options={levelOptions}
                                />
                            </div>
                            <div>
                                <KPISelector
                                    isVisible={isPopoverVisible}
                                    setIsVisible={setIsPopoverVisible}
                                    kpiList={
                                        kpiList?.map((p) => {
                                            return {
                                                value: p.name?.toString(),
                                                label: p.label,
                                                isSelected: !!tableData.find(
                                                    (m: OptimisationPlanData) => m.name === p.name,
                                                ),
                                            };
                                        }) || []
                                    }
                                    onSubmit={onKPISelectHandler}
                                    buttonName="Select Constraints"
                                    searchPlaceholder="Search KPI..."
                                    variableName={'KPI'}
                                    showSelectAll
                                    addDisabled={!kpiList.length || tableContentLoading}
                                    disableMinimumOneSelectionCondition
                                />
                            </div>
                        </div>

                        <div className="flex-1 min-h-0 overflow-hidden">
                            {errorExists ? (
                                <div className="flex justify-center items-center h-full text-red-600">
                                    {'An unexpected error occurred. Please try again later.'}
                                </div>
                            ) : tableContentLoading ? (
                                <div className="flex justify-center items-center h-full">
                                    <Spin />
                                </div>
                            ) : tableData.length ? (
                                <OptimizePlanTable
                                    tableData={tableData}
                                    setTableData={setTableData}
                                    headerColumns={tableHeaders}
                                    level={level}
                                />
                            ) : (
                                <div className="flex justify-center items-center h-full">Select Constraints</div>
                            )}
                        </div>
                    </div>
                </div>
            </CustomModal>
        </div>
    );
};

export default OptimizePlanModal;
